import { jsx, jsxs } from 'react/jsx-runtime';
import { useContextAccessToken, useContextShareToken } from '../../../permission/contexts/AnonShareTokenContext.js';
import { useQueryResourceShareTokens } from '../../../permission/queries/useQueryResourceShareTokens.js';
import { getPublicShareToken } from '../../../permission/utils/getPublicShareToken.js';
import { useQueryUserInfo } from '../../../user/queries/useQueryUserInfo.js';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { ForcedTheme } from '@fabric-app/ui/theme/ForcedTheme';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { getUserColorFromId } from '@fabric-app/utils/color';
import { IconUserPlus, IconResourcePreview30Shared } from '@fabric-app/icons/svg';
import { PermissionRole } from '@fabric/woody-client';
import React__default, { useMemo } from 'react';
import { v4 } from 'uuid';
import UserTooltip from './UserTooltip.js';
import { isTouchScreen } from '@fabric-app/ui/utils/isTouchScreen';
import { Button } from '@fabric-app/ui/components/Button';
import Divider from '@fabric-app/ui/components/Divider';
import DropdownMemberItem from './DropdownMemberItem.js';
import { styled, css } from 'styled-components';
import { preventForwardPropsConfig } from '@fabric-app/ui/utils/preventForwardProps';
import Avatar from '../../../user/components/Avatar/Avatar.js';
import { mediaHover, mediaNotMobile } from '@fabric-app/ui/constants/styled-utils';
import AvatarUser from '../../../user/components/AvatarUser/AvatarUser.js';
import { motion } from 'framer-motion';
import { framerAnimationFade } from '@fabric-app/ui/constants/framerAnimations';

const simplifyMember = (member, liveMembers = void 0, options) => {
  const fromLiveblocks = "info" in member;
  return {
    id: member.id,
    name: fromLiveblocks ? member.info.name : member.name || "Unnamed",
    picture: fromLiveblocks ? member.info.picture : member.pictureUrlCdn,
    color: getUserColorFromId(member.id ?? v4()),
    role: fromLiveblocks ? options.defaultRole : member.role,
    online: fromLiveblocks || !!liveMembers?.some((m) => m.id === member.id)
  };
};
const defaultLiveMembers = [];
const defaultMembers = [];
const MemberBar = ({ anonId, followUser: _followUser, spotlightSelf, following, className, maxMembers: maxMembersProp = 3, noBorder = false, spaceOverviewViewMode, hideWhenAlone = false, onClickOpenSharingSettings, hideWrappingContainer, avatarSize = 30, resourceId, itemType, barColorScheme, isPublicShared, hideShareButton, ...props }) => {
  const { data: user } = useQueryUserInfo();
  const [open, onOpenChange] = React__default.useState(false);
  const accessToken = useContextAccessToken();
  const handleFollow = _followUser ? (userId, e) => {
    onOpenChange(false);
    _followUser(userId, e);
  } : void 0;
  const liveMembersProp = "liveMembers" in props && props.liveMembers;
  const showOfflineOnlineAvatarState = Array.isArray(liveMembersProp);
  const liveMembers = useMemo(() => {
    const members2 = liveMembersProp || defaultLiveMembers;
    return members2.map((m) => {
      if (m.id === anonId) {
        return {
          ...m,
          name: user?.name || m.info.name
        };
      }
      return m;
    });
  }, [
    liveMembersProp,
    anonId,
    user
  ]);
  const members = ("members" in props ? props.members : props.partialMembersList) || defaultMembers;
  const permissions = useQueryResourceShareTokens(resourceId, {
    enabled: !accessToken
  });
  const contextShareToken = useContextShareToken();
  const publicShareToken = getPublicShareToken(permissions.data || []);
  const defaultRole = publicShareToken?.role || contextShareToken && "role" in contextShareToken && contextShareToken?.role || PermissionRole.VIEWER;
  const simplifiedMembers = useMemo(() => {
    const allMembers = [
      ...members,
      ...liveMembers
    ].map((member) => simplifyMember(member, liveMembers, {
      defaultRole
    })).filter((m, _, arr) => arr.findIndex((a) => a.id === m.id) === arr.lastIndexOf(m));
    const sortedMembers = allMembers.sort((a, b) => {
      if (a.role === PermissionRole.OWNER) return -1;
      if (b.role === PermissionRole.OWNER) return 1;
      if (a.id === user?.id) return -1;
      if (b.id === user?.id) return 1;
      if (a.online && !b.online) return -1;
      if (!a.online && b.online) return 1;
      return 0;
    });
    return sortedMembers;
  }, [
    members,
    liveMembers,
    defaultRole,
    user?.id
  ]);
  const totalMembers = "totalMembers" in props ? props.totalMembers || 0 : simplifiedMembers.length;
  const maxMembers = maxMembersProp + 1 >= totalMembers ? maxMembersProp + 1 : maxMembersProp;
  const hiddenMembersCount = useMemo(() => {
    return Math.max(0, totalMembers - maxMembers);
  }, [
    totalMembers,
    maxMembers
  ]);
  const isAlone = simplifiedMembers.length === 1 && simplifiedMembers[0].id === user?.id || simplifiedMembers.length === 0;
  const { isMobileView } = useResponsive();
  if (!members && !liveMembers && !onClickOpenSharingSettings || members?.length === 0 && liveMembers?.length === 0 && !onClickOpenSharingSettings || isAlone && hideWhenAlone && (!onClickOpenSharingSettings || hideShareButton)) {
    return null;
  }
  const handleOpenSharingSettings = onClickOpenSharingSettings ? (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClickOpenSharingSettings(e);
  } : void 0;
  return /* @__PURE__ */ jsx(motion.div, {
    ...framerAnimationFade,
    children: /* @__PURE__ */ jsxs(DropdownMenu.Base, {
      openProps: {
        open,
        onOpenChange
      },
      contentProps: {
        style: {
          padding: 8,
          borderRadius: "1rem",
          background: cssVar["color-bg-primary"],
          width: isMobileView ? void 0 : "auto",
          minWidth: 380,
          maxWidth: 460
        }
      },
      renderTriggerElement: (props2) => {
        return /* @__PURE__ */ jsx(ForcedTheme, {
          colorScheme: barColorScheme,
          asChild: true,
          children: /* @__PURE__ */ jsxs(Container, {
            ...props2,
            "data-test-container": "member-bar",
            style: {
              cursor: liveMembers ? "pointer" : "default",
              border: noBorder || spaceOverviewViewMode === "List" ? "none" : void 0
            },
            hideWrappingContainer,
            children: [
              isPublicShared && /* @__PURE__ */ jsx(Avatar, {
                tooltip: "This item is shared",
                size: avatarSize,
                fixedBgColor: cssVar["color-bg-quarternary"],
                children: /* @__PURE__ */ jsx(IconResourcePreview30Shared, {
                  style: {
                    color: `rgba(${cssVar["color-bg-primary-reverse-rgb"]}, 0.5)`
                  }
                })
              }),
              !isAlone && simplifiedMembers.slice(0, maxMembers).map((member, index) => /* @__PURE__ */ jsx(AvatarUserStacked, {
                user: member,
                allowTooltipHover: open ? false : liveMembers ? true : false,
                state: showOfflineOnlineAvatarState ? member.online ? void 0 : "offline" : void 0,
                size: avatarSize,
                style: {
                  zIndex: maxMembers - index
                },
                tooltip: open ? null : /* @__PURE__ */ jsx(UserTooltip, {
                  member,
                  showFollowControls: liveMembers.length > 1,
                  followUser: handleFollow,
                  spotlightSelf,
                  following,
                  anonId
                })
              }, member.id)),
              hiddenMembersCount > 0 && /* @__PURE__ */ jsxs(HiddenMembersCount, {
                style: {
                  zIndex: maxMembers + 1,
                  height: avatarSize
                },
                children: [
                  "+",
                  hiddenMembersCount
                ]
              }),
              !hideShareButton && onClickOpenSharingSettings && !accessToken && /* @__PURE__ */ jsx(ManageSharingButton, {
                size: avatarSize,
                color: cssVar["color-app-primary-cmp"],
                // just to prevent default behavior of the radix trigger
                onPointerDown: !isTouchScreen() ? handleOpenSharingSettings : void 0,
                onClick: isTouchScreen() ? handleOpenSharingSettings : void 0,
                children: /* @__PURE__ */ jsx(IconUserPlus, {
                  style: {
                    color: cssVar["color-app-primary"],
                    fontSize: ".55em"
                  }
                })
              })
            ]
          })
        });
      },
      children: [
        onClickOpenSharingSettings && !accessToken && /* @__PURE__ */ jsx(DropdownMenu.Group, {
          children: /* @__PURE__ */ jsxs(Button, {
            size: "lg",
            variant: !isMobileView ? "bg-tertiary" : "bg-secondary",
            style: {
              justifyContent: "flex-start",
              width: "100%",
              gap: 17,
              height: 48,
              fontSize: 14,
              fontWeight: 500,
              paddingLeft: 11,
              ...!isMobileView && {
                background: "transparent"
              }
            },
            onClick: (e) => {
              onOpenChange(false);
              onClickOpenSharingSettings(e);
            },
            children: [
              /* @__PURE__ */ jsx(IconUserPlus, {
                style: {
                  fontSize: "1.25rem"
                }
              }),
              /* @__PURE__ */ jsx("span", {
                children: "Manage sharing"
              })
            ]
          })
        }),
        !isMobileView && !!onClickOpenSharingSettings && /* @__PURE__ */ jsx("div", {
          style: {
            padding: ".5rem 0"
          },
          children: /* @__PURE__ */ jsx(Divider, {
            borderColor: "tertiary"
          })
        }),
        /* @__PURE__ */ jsx(DropdownMenu.Group, {
          style: !isMobileView ? {
            gap: 4,
            display: "flex",
            flexDirection: "column"
          } : void 0,
          children: simplifiedMembers.map((m) => /* @__PURE__ */ jsx(DropdownMemberItem, {
            itemType,
            member: m,
            anonId,
            followUser: handleFollow,
            spotlightSelf,
            following,
            membersOnlineCount: liveMembers?.length || 1
          }, m.id))
        })
      ]
    })
  });
};
const Container = styled(DropdownMenu.Trigger).withConfig(preventForwardPropsConfig([
  "hideWrappingContainer"
])).withConfig({
  displayName: "MemberBar__Container",
  componentId: "sc-9c7cc8c4-0"
})([
  `border-radius:100px;background:`,
  `;padding:5px;display:flex;align-items:center;justify-content:center;width:min-content;gap:2px;`,
  ``
], cssVar["color-bg-secondary-button"], (p) => p.hideWrappingContainer ? css([
  `padding:0 !important;border:none !important;background:none !important;`
]) : css([
  `transition:0.2s all;`,
  `{&:hover:not(:disabled){box-shadow:0 0 10rem 0 rgba(`,
  `,0.05) inset;}}`
], mediaHover, cssVar["color-bg-primary-reverse-rgb"]));
const ManageSharingButton = styled(Avatar).withConfig({
  displayName: "MemberBar__ManageSharingButton",
  componentId: "sc-9c7cc8c4-1"
})([
  `&:not(:first-child){margin-left:2px;}`
]);
const HiddenMembersCount = styled.div.withConfig({
  displayName: "MemberBar__HiddenMembersCount",
  componentId: "sc-9c7cc8c4-2"
})([
  `display:flex;align-items:center;padding:0 4px;font-weight:600;font-size:0.75rem;color:`,
  `;`,
  `{border-radius:100px;height:100%;padding:0 8px;margin-left:-6px;}`
], cssVar["color-text-placeholder"], mediaNotMobile);
const AvatarUserStacked = styled(AvatarUser).withConfig({
  displayName: "MemberBar__AvatarUserStacked",
  componentId: "sc-9c7cc8c4-3"
})([
  `&:not(:first-child){margin-left:-6px;}`
]);

export { MemberBar as default };
//# sourceMappingURL=MemberBar.js.map
